import * as React from "react"
import {
    DroneMake,
    getDroneMakeDisplayName,
    getDroneModelDisplayName,
    normalizeDroneMake,
    normalizeDroneModel,
} from "@nova/constants"
import { getDroneImageUrl, getUnknownDroneImageUrl, DroneImageQuality } from "./drone-images"
import { useCachedImage, useImagePadding } from "@nova/toolkit-pure-hooks"
import { useCallback, useEffect, useMemo } from "react"

type UseAircraftImageProps = {
    make: string | null | undefined
    model: string | null | undefined
    quality: DroneImageQuality
}
export type AircraftImageProps = UseAircraftImageProps &
    Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src">

// Generic helper keeps M opaque so DroneModel<M> doesn't collapse to never
function resolveKnownMake<M extends DroneMake>(
    make: M,
    rawModel: string,
    quality: DroneImageQuality
): { src: string; defaultAlt: string } | null {
    const model = normalizeDroneModel(make, rawModel)
    if (!model) return null
    return {
        src: getDroneImageUrl(make, model, quality),
        defaultAlt: `${getDroneMakeDisplayName(make)} ${getDroneModelDisplayName(make, model)} drone`,
    }
}

function resolveAircraftImageData(
    rawMake: string,
    rawModel: string,
    quality: DroneImageQuality
): { src: string; defaultAlt: string } | null {
    const make = normalizeDroneMake(rawMake)
    if (!make) return null
    return resolveKnownMake(make, rawModel, quality)
}

export function AircraftImage({
    make: rawMake,
    model: rawModel,
    quality,
    alt,
    ...imgProps
}: AircraftImageProps) {
    const resolved =
        rawMake && rawModel ? resolveAircraftImageData(rawMake, rawModel, quality) : null

    const unknownSrc = getUnknownDroneImageUrl(quality)

    const style: React.CSSProperties = {
        imageRendering: "auto",
        transform: "translateZ(0)",
        filter: "blur(0)",
        WebkitFontSmoothing: "antialiased",
        ...imgProps.style,
    }

    if (resolved) {
        return (
            <img
                src={resolved.src}
                alt={alt ?? resolved.defaultAlt}
                onError={(e) => {
                    e.currentTarget.src = unknownSrc
                }}
                {...imgProps}
                style={style}
            />
        )
    }

    return <img src={unknownSrc} alt={alt ?? "Unknown drone"} {...imgProps} style={style} />
}

export function useAircraftImage({
    make: rawMake,
    model: rawModel,
    quality,
}: UseAircraftImageProps) {
    const resolved = useMemo(
        () => (rawMake && rawModel ? resolveAircraftImageData(rawMake, rawModel, quality) : null),
        [quality, rawMake, rawModel]
    )
    const fetchBlob = useCallback(async () => {
        if (resolved) {
            try {
                const response = await fetch(resolved.src)
                if (!response.ok) throw new Error()
                return response.blob()
            } catch {
                // continue to the unknown image
            }
        }
        const response = await fetch(getUnknownDroneImageUrl(quality))
        if (!response.ok) throw new Error()
        return response.blob()
    }, [quality, resolved])
    const { blobUrl, isError, isFetching, fetchBlobUrl } = useCachedImage({
        type: "Aircraft",
        imageKey: `${rawMake}-${rawModel}-${quality}`,
        fetchBlob,
    })
    const imagePadding = useImagePadding(blobUrl)
    useEffect(() => {
        fetchBlobUrl()
    }, [fetchBlobUrl])
    return {
        blobUrl,
        alt: resolved?.defaultAlt ?? "Unknown drone",
        isError,
        isFetching,
        imagePadding,
    }
}

export * from "./drone-images"
